import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

/**
 * Scraper health, served by aloha.api.tender from the scraper_health table.
 *
 * That table is materialised every few minutes by an evaluator in aloha.indexer, which is the only
 * process holding a connection to every per-source scraper database. Nothing on this path queries
 * those databases: they hold the live job queues the scrapers are competing for, so a dashboard
 * refresh must never aggregate over them.
 */
class ScraperHealthService {
  /** One entry per source with its lanes, problems already sorted to the top by the API. */
  getOverview() {
    return ApiService.get(`${Config.api.tender.url}scraper-health`).then((response) => mapOverview(response.data));
  }

  getSource(sourceId) {
    return ApiService.get(`${Config.api.tender.url}scraper-health/${sourceId}`).then((response) =>
      mapSource(response.data),
    );
  }

  /** A reason is required; who paused it comes from the JWT, not from here. */
  pauseSource({ sourceId, reason }) {
    return ApiService.post(`${Config.api.tender.url}scraper-health/${sourceId}/pause`, {
      reason,
    }).then((response) => response.data);
  }

  resumeSource(sourceId) {
    return ApiService.post(`${Config.api.tender.url}scraper-health/${sourceId}/resume`, {}).then(
      (response) => response.data,
    );
  }
}

const mapLane = (dto) => ({
  sourceId: dto.sourceId,
  kind: dto.kind,
  state: dto.state,
  // The underlying state even while paused, so the drawer can show whether a paused source has
  // recovered — which is what you need before deciding to un-pause it.
  computedState: dto.computedState,
  reason: dto.reason,
  since: dto.since,
  problemSince: dto.problemSince,
  evaluatedAt: dto.evaluatedAt,
  scrapeExpected: dto.scrapeExpected,
  lastSuccessAt: dto.lastSuccessAt,
  lastRunAt: dto.lastRunAt,
  executedCount: dto.executedCount,
  failedCount: dto.failedCount,
  failureRatio: dto.failureRatio,
  failedOpenCount: dto.failedOpenCount,
  failedOldestAt: dto.failedOldestAt,
  tendersCount: dto.tendersCount,
  solrCount: dto.solrCount,
  solrNewestAt: dto.solrNewestAt,
  lastError: dto.lastError,
  lastErrorAt: dto.lastErrorAt,
  pausedAt: dto.pausedAt,
  pausedBy: dto.pausedBy,
  pausedReason: dto.pausedReason,
});

const mapSource = (dto) => {
  const lanes = (dto.lanes || []).map(mapLane);
  const laneByKind = (kind) => lanes.find((l) => l.kind === kind) || null;

  return {
    sourceId: dto.sourceId,
    overallState: dto.overallState,
    lanes,
    // Named lanes so the table can bind columns directly. A listing-only source (canada_buys,
    // nunavut) has no scrape lane at all, and null must render as "not applicable" rather than as
    // a problem — the absence is by design, not a gap.
    crawl: laneByKind('crawl'),
    scrape: laneByKind('scrape'),
    index: laneByKind('index'),
    evaluatedAt: dto.evaluatedAt,
    failedOpenCount: dto.failedOpenCount,
    paused: dto.paused,
  };
};

const mapOverview = (dto) => ({
  sources: (dto.sources || []).map(mapSource),
  canary: dto.canary ? mapLane(dto.canary) : null,
  problemCount: dto.problemCount,
  pausedCount: dto.pausedCount,
});

export const scraperHealthService = new ScraperHealthService();
